import {
  Image,
  Box,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import container from "../images/container.png";
import closeIcon from "../images/cross.png";
import top1Icon from "../images/top1.png";
import top2Icon from "../images/top2.png";
import "../announcement.css";
import AnnouncementContent from "./AnnouncementContent";
import MaintenanceContent from "./MaintenanceContent";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const DISPLAY_CONTENT = {
  ANNOUNCEMENT: "announcement",
  MAINTENANCE: "maintenance",
};

/**
 * @description 公告彈窗
 * @returns {JSX.Element}
 */
const AnnouncementModal = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const openModalParams = searchParams.get("announcement_open");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(DISPLAY_CONTENT.ANNOUNCEMENT);
  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const reduxAnnouncementList = useSelector(
    (state) => state.isAnnouncementList,
  );
  const { allGamePlatform = [] } = isHandleAllGame;

  // 從 allGamePlatform 中篩選出有維護時間的遊戲平台
  const maintenanceList = allGamePlatform.filter((item) => {
    const isOpenStart =
      item.maintenanceStartTime && item.maintenanceStartTime !== "00:00:00";
    const isOpenEnd =
      item.maintenanceEndTime && item.maintenanceEndTime !== "00:00:00";
    return isOpenStart || isOpenEnd;
  });

  const handleCloseModal = () => setModalOpen(false);

  // 切換公告與維護通知的內容
  const handleTabClick = () => {
    if (activeTab === DISPLAY_CONTENT.ANNOUNCEMENT) {
      setActiveTab(DISPLAY_CONTENT.MAINTENANCE);
    } else {
      setActiveTab(DISPLAY_CONTENT.ANNOUNCEMENT);
    }
  };

  // 根據 URL 參數控制 Modal 的顯示
  useEffect(() => {
    if (openModalParams === "true") {
      setModalOpen(true);
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete("announcement_open");
      setSearchParams(currentParams.toString());
    }
  }, [openModalParams, location.search, setSearchParams]);

  // 當初始化取得公告資料時，自動彈出彈窗（每次會話只彈出一次）
  useEffect(() => {
    // 檢查是否已經顯示過公告（使用 sessionStorage，刷新頁面後會重置）
    const hasShownAnnouncement = sessionStorage.getItem("hasShownAnnouncement");

    // 如果有公告數據且尚未顯示過，自動打開彈窗
    if (reduxAnnouncementList.list?.length > 0 && !hasShownAnnouncement) {
      setModalOpen(true);
      // 標記已經顯示過公告
      sessionStorage.setItem("hasShownAnnouncement", "true");
    }
  }, [reduxAnnouncementList.list?.length]);

  useEffect(() => {
    if (reduxAnnouncementList.list?.length > 0) {
      setActiveTab(DISPLAY_CONTENT.ANNOUNCEMENT);
    } else {
      setActiveTab(DISPLAY_CONTENT.MAINTENANCE);
    }
  }, [reduxAnnouncementList.list?.length]);

  return (
    <Modal isOpen={modalOpen} isCentered>
      <ModalOverlay />
      <ModalContent
        position={"relative"}
        background={"transparent"}
        bgImage={container}
        bgSize={"100% 100%"}
        height={{ base: "450px", sm: "600px" }}
        maxW={{ base: "100%", sm: "500px" }}
        padding={{ base: "5em 4.7em 1em 2.5em", sm: "6.5em 7em 1em 3.5em" }}
        marginX={"1em"}
        shadow={"none"}
      >
        {/* 關閉按鈕 */}
        <Box
          onClick={handleCloseModal}
          cursor={"pointer"}
          height={{ base: "3em", sm: "4em" }}
          width={{ base: "3em", sm: "4em" }}
          position={"absolute"}
          top={"-1em"}
          right={"2em"}
          _hover={{ transform: "scale(1.1)", transition: "transform 0.2s" }}
        >
          <Image
            src={closeIcon}
            alt="Close"
            cursor="pointer"
            width={"100%"}
            height={"100%"}
            objectFit="cover"
          />
        </Box>

        <Box
          position={"absolute"}
          top={{ base: "0em", sm: "0.4em" }}
          left={{ base: "11em", sm: "12.5em" }}
          cursor={"pointer"}
          _hover={{ transform: "scale(1.1)", transition: "transform 0.2s" }}
          onClick={handleTabClick}
          width={{ base: "6rem", sm: "7rem" }}
          height={{ base: "2.5em", sm: "3em" }}
          display={"flex"}
          alignItems={"end"}
          justifyContent={"center"}
          bgImage={top2Icon}
          bgSize={"100% 100%"}
        >
          <Text
            className="kinkaku"
            fontSize={{ base: "1rem", sm: "1rem" }}
            bgGradient={"linear(to-b, #fff0cc, #f6b86b, #fedf71)"}
            bgClip={"text"}
            sx={{ filter: "drop-shadow(1px 2px 3px #000000)" }}
          >
            {activeTab === DISPLAY_CONTENT.ANNOUNCEMENT
              ? t(`announcement.${DISPLAY_CONTENT.MAINTENANCE}`)
              : t(`announcement.${DISPLAY_CONTENT.ANNOUNCEMENT}`)}
          </Text>
        </Box>

        <Box
          position={"absolute"}
          top={{ base: "-2.5em", sm: "-2.5em" }}
          left={{ base: "2em", sm: "2em" }}
          alignItems={"end"}
          justifyContent={"center"}
          display={"flex"}
          width={{ base: "10em", sm: "12em" }}
          height={{ base: "5em", sm: "6em" }}
          bgImage={top1Icon}
          bgSize={"100% 100%"}
          paddingLeft={"1em"}
        >
          <Text
            className="kinkaku"
            fontSize={{ base: "1.5rem", sm: "2.5rem" }}
            bgGradient={"linear(to-b, #fff0cc, #f6b86b, #fedf71)"}
            bgClip={"text"}
            sx={{ filter: "drop-shadow(1px 2px 3px #000000)" }}
          >
            {activeTab === DISPLAY_CONTENT.ANNOUNCEMENT
              ? t(`announcement.${DISPLAY_CONTENT.ANNOUNCEMENT}`)
              : t(`announcement.${DISPLAY_CONTENT.MAINTENANCE}`)}
          </Text>
        </Box>

        <Box height={"100%"} width={"100%"} overflow={"hidden"}>
          {/* 根據 activeTab 顯示不同的內容 */}
          {activeTab === DISPLAY_CONTENT.ANNOUNCEMENT ? (
            <AnnouncementContent
              reduxAnnouncementList={reduxAnnouncementList}
            />
          ) : (
            <MaintenanceContent maintenanceList={maintenanceList} />
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default AnnouncementModal;
